/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRouter, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { Community } from "@packages/sdk";
import {
  useActiveSidebarTab,
  useRequestCommunitiesAdminCommunity,
  useRequestMutationsCommunitiesAdminCommunity,
} from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  constantSemanticColors,
  dropShadow,
  IconButton,
  MinusIcon,
  ModalDropdownMenu,
  ModalDropdownMenuItem,
  MoreIcon,
  spacing,
  useAlert,
} from "@packages/ui";

import { getBasePADUrl, padTabsPaths } from "../../../../lib";

const dropdownMenuItemStyles: Interpolation = {
  columnGap: spacing.s,
  minWidth: "225px",
  padding: `${spacing.xs} ${spacing.s}`,
};

const dropdownMenuStyles: Interpolation = {
  boxShadow: `${dropShadow.none} ${dropShadow.ms} ${dropShadow.m} ${dropShadow.none} ${constantSemanticColors.fixedDarkenVeryLow}`,
};

const dropdownMenuItemErrorStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  color: theme.semanticColors.errorMedium,
});

const dropdownMenuItemContentStyles: Interpolation = {
  alignItems: "center",
  columnGap: spacing.s,
  display: "flex",
};

type EditGroupDropdownMenuProps = {
  communityId: number;
  childCommunity: Pick<Community, "id" | "name">;
  triggerCSS?: Interpolation;
};

export const EditGroupDropdownMenu = ({
  communityId,
  childCommunity,
  triggerCSS,
}: EditGroupDropdownMenuProps) => {
  const t = useTranslations();
  const alert = useAlert({ button: "Ok" });
  const analytics = useAnalytics();
  const router = useRouter();

  const activeSidebarTab = useActiveSidebarTab({
    baseUrl: getBasePADUrl({ communityId }),
    tabs: padTabsPaths,
  });

  const { query: communityQuery } = useRequestCommunitiesAdminCommunity({
    id: communityId,
  });
  const { mutationUnlink: unlinkChildCommunity } =
    useRequestMutationsCommunitiesAdminCommunity({ id: communityId });

  const handleUnlinkGroup = () => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedUnlinkSmallGroup,
      properties: {
        small_group_id: childCommunity.id,
        small_group_name: childCommunity.name,
        parent_id: communityId,
        parent_name: communityQuery.data?.name,
      },
    });
    unlinkChildCommunity.mutate(
      { id: childCommunity.id },
      {
        onError: (error) =>
          alert.show({
            title: t("general_word_error"),
            description: error.message,
          }),
        onSuccess: () => {
          router.push(`/admin/${communityId}/${activeSidebarTab}`);
        },
      },
    );
  };

  return (
    <ModalDropdownMenu
      trigger={
        <IconButton
          icon={<MoreIcon />}
          variant="transparentNeutral"
          css={triggerCSS}
          onPointerDown={() => {
            analytics.track({
              event: HallowAnalyticsEvent.TappedCommunityMemberOptions,
            });
          }}
        />
      }
      contentCSS={[dropdownMenuStyles]}
    >
      <ModalDropdownMenuItem
        modalItemProps={{
          onPointerDown: (e) => {
            e.stopPropagation();
            handleUnlinkGroup();
          },
        }}
        dropdownMenuItemProps={{
          onClick: (e) => {
            e.stopPropagation();
            handleUnlinkGroup();
          },
        }}
        dropdownMenuItemCSS={[
          dropdownMenuItemStyles,
          dropdownMenuItemErrorStyles,
        ]}
        modalItemCSS={[dropdownMenuItemStyles, dropdownMenuItemErrorStyles]}
      >
        <div css={dropdownMenuItemContentStyles}>
          <MinusIcon.Outline />
          {t("community_member_option_remove")}
        </div>
      </ModalDropdownMenuItem>
    </ModalDropdownMenu>
  );
};
